'use client';

import { AppImageClient, AppImageData } from '@components/appImage';
import { Card } from '@components/card';
import { ChipWithLinearGradient } from '@components/chipWithLinearGradient';
import { CTAButtons } from '@components/ctaButtons';
import { ParseWithTooltip } from '@components/parseWithTooltip';
import { ScrollToFormButton } from '@components/scrollToFormButton';
import {
  TabsContentSection,
  TabSectionItem,
} from '@components/tabsContentSection';
import { Typography } from '@components/typography';

import { TranslatedComponent } from '@/shared/types';

interface IntegrationTabsSectionProps extends TranslatedComponent {
  items: IntegrationTabItem[];
  /** Данные AppImage по imgSrcKey из `getAppImagesData`. */
  imagesDataMap: Record<string, AppImageData>;
}

export interface IntegrationTabItem extends TabSectionItem {
  title: string;
  description: string;
  features: string[];
  button?: {
    href?: string;
    label: string;
  };
  imgSrcKey: string;
}

export const IntegrationTabsSection = ({
  items,
  imagesDataMap,
  translations,
}: IntegrationTabsSectionProps) => {
  return (
    <TabsContentSection items={items}>
      {(activeItem) => (
        <>
          {/** WEB */}
          <div className="flex flex-col gap-6 max-sm:hidden">
            <Card variant="subtle">
              <div className="flex gap-7 p-8">
                <div className="flex w-full flex-col justify-between gap-8">
                  <div className="flex flex-col gap-5">
                    <div className="flex flex-col gap-3">
                      <Typography variant="heading-s">
                        {activeItem.title}
                      </Typography>
                      <Typography variant="text-base">
                        <ParseWithTooltip
                          position="bottom-start"
                          text={activeItem.description}
                          tooltipContent={translations.integrationTooltip}
                        />
                      </Typography>
                    </div>

                    <div className="flex flex-wrap gap-[10px]">
                      {activeItem.features.map((feature) => (
                        <ChipWithLinearGradient
                          key={feature}
                          spacing="compact"
                          rounded="12"
                        >
                          <Typography variant="text-base" color="secondary">
                            {feature}
                          </Typography>
                        </ChipWithLinearGradient>
                      ))}
                    </div>
                  </div>

                  {activeItem.button && (
                    <div>
                      <CTAButtons
                        secondaryButton={{
                          ...activeItem.button,
                          spacing: 'normal',
                        }}
                      />
                    </div>
                  )}
                </div>

                <div className="flex h-auto w-[470px] shrink-0 items-center justify-center">
                  <AppImageClient
                    key={activeItem.imgSrcKey}
                    {...imagesDataMap[activeItem.imgSrcKey]}
                    alt={activeItem.title}
                    className="h-auto w-full object-contain"
                  />
                </div>
              </div>
            </Card>

            <Card className="flex items-center justify-between p-2 pl-7">
              <span className="text-[18px] leading-[28px] font-medium tracking-[-0.18px]">
                {translations.integrationCta}
              </span>
              <ScrollToFormButton variant="primary" spacing="normal">
                {translations.buttonGetConsultation}
              </ScrollToFormButton>
            </Card>
          </div>

          {/** MOBILE */}
          <div className="flex flex-col gap-6 pr-4 md:hidden">
            <Card variant="subtle">
              <div className="flex flex-col gap-6 p-5">
                <div className="flex flex-col gap-8">
                  <div className="flex w-full flex-col justify-between gap-5">
                    <div className="flex flex-col gap-3">
                      <Typography variant="heading-s">
                        {activeItem.title}
                      </Typography>
                      <Typography variant="text-base">
                        <ParseWithTooltip
                          position="bottom-start"
                          text={activeItem.description}
                          tooltipContent="Личный кабинет для продвижения бизнеса в 2ГИС: карточка компании, отзывы, реклама, аналитика"
                        />
                      </Typography>
                    </div>

                    <div className="flex flex-wrap gap-3">
                      {activeItem.features.map((feature) => (
                        <ChipWithLinearGradient
                          key={feature}
                          spacing="compact"
                          rounded="12"
                        >
                          <Typography variant="text-base" color="secondary">
                            {feature}
                          </Typography>
                        </ChipWithLinearGradient>
                      ))}
                    </div>
                  </div>

                  <div className="flex h-auto w-[300px] shrink-0 items-center justify-center">
                    <AppImageClient
                      key={activeItem.imgSrcKey}
                      {...imagesDataMap[activeItem.imgSrcKey]}
                      alt={activeItem.title}
                      className="h-auto w-full object-contain"
                    />
                  </div>
                </div>
                {activeItem.button && (
                  <div>
                    <CTAButtons
                      secondaryButton={{
                        ...activeItem.button,
                        spacing: 'normal',
                      }}
                    />
                  </div>
                )}
              </div>
            </Card>

            <Card className="flex flex-col gap-6 p-4">
              <span className="text-[18px] leading-[28px] font-medium tracking-[-0.18px]">
                {translations.integrationCta}
              </span>
              <ScrollToFormButton
                variant="primary"
                spacing="normal"
                className="flex items-center justify-center"
              >
                {translations.buttonGetConsultation}
              </ScrollToFormButton>
            </Card>
          </div>
        </>
      )}
    </TabsContentSection>
  );
};
